import { nanoid } from '@reduxjs/toolkit'
import type { ReduxQuote, ReduxRecapInfo } from 'src/types/apiType'
import type { Category, SubCategory } from 'src/types/jsonType'
import type { QuoteType } from 'src/types/miscType'
import {
	aicpCategories,
	aicpSubCategories,
	categories,
	cnc5chCategories,
	cnc5chSubCategories,
	photoGridCategories,
	photoGridSubCategories,
	postProductionCategories,
	quote360TemplateCategories,
	quoteAnimationAndLiveActionCategories,
	quoteAnimationAndLiveActionSubCategories,
	quoteAnimationOnlyCategories,
	quoteAnimationOnlySubCategories,
	quoteDigitalCategories,
	quoteDigitalSubCategories,
	soundProdCategories,
	soundProdSubCategories,
} from 'src/utils/quoteGridUtils/createQuoteUtil'
import { getCreateQuoteRecapInfoUtil } from 'src/utils/recapGridUtils/createQuoteRecapInfoUtil/getCreateQuoteRecapInfoUtil'

function createInvoiceQuoteCostlines({
	quoteDetails,
}: {
	quoteDetails: ReduxQuote
}) {
	// * We change the quote type to blankQuoteType if the quote type is blankQuote else we keep the original quote type
	const quoteType =
		quoteDetails.quoteType === 'blankQuote'
			? (quoteDetails.blankQuoteType as QuoteType)
			: quoteDetails.quoteType

	let newCostlinesArray = []
	const { createRecapInfoCostLineIdsAndLabels } =
		getCreateQuoteRecapInfoUtil(quoteType)
	switch (quoteType) {
		case 'frenchApfp':
			newCostlinesArray = createCostlinesForCategoryDefaults({
				categoriesArray: categories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'frenchPostProd':
			newCostlinesArray = createCostlinesForCategoryDefaults({
				categoriesArray: postProductionCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'aicp':
			newCostlinesArray = createCostlinesForSubCategoryDefaults({
				categoriesArray: aicpCategories,
				subCategoriesArray: aicpSubCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'photoGrid':
			newCostlinesArray = createCostlinesForSubCategoryDefaults({
				categoriesArray: photoGridCategories,
				subCategoriesArray: photoGridSubCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'soundProd':
			newCostlinesArray = createCostlinesForSubCategoryDefaults({
				categoriesArray: soundProdCategories,
				subCategoriesArray: soundProdSubCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'cnc5ch':
			newCostlinesArray = createCostlinesForSubCategoryDefaults({
				categoriesArray: cnc5chCategories,
				subCategoriesArray: cnc5chSubCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'quoteAnimationOnly':
			newCostlinesArray = createCostlinesForSubCategoryDefaults({
				categoriesArray: quoteAnimationOnlyCategories,
				subCategoriesArray: quoteAnimationOnlySubCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'quoteAnimationAndLiveAction':
			newCostlinesArray = createCostlinesForSubCategoryDefaults({
				categoriesArray: quoteAnimationAndLiveActionCategories,
				subCategoriesArray: quoteAnimationAndLiveActionSubCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case 'blankQuote':
			// No invoice quote created for blank quote we should be creating it for blankQuoteType
			return
		case 'quoteDigital':
			newCostlinesArray = createCostlinesForSubCategoryDefaults({
				categoriesArray: quoteDigitalCategories,
				subCategoriesArray: quoteDigitalSubCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		case '360Template':
			newCostlinesArray = createCostlinesForCategoryDefaults({
				categoriesArray: quote360TemplateCategories,
				quoteDetails,
			})
			return createRecapInfoCostLineIdsAndLabels(
				newCostlinesArray,
				quoteDetails.id as string,
				quoteDetails.tenantId as string,
				quoteDetails.childFreelancers as string[]
			)
		default: {
		}
		// Do nothing
	}
}

function createCostlinesForCategoryDefaults({
	categoriesArray,
	quoteDetails,
}: {
	categoriesArray: Category[]
	quoteDetails: ReduxQuote
}) {
	const newCostlinesArray = [] as ReduxRecapInfo[]

	categoriesArray.map((c) => {
		const newSubCategoryTotalCostline = {
			id: nanoid(),
			activeFlag: true,
			quoteId: quoteDetails.id,
			tenantId: quoteDetails.tenantId,
			freelancers: quoteDetails.freelancers,
			childFreelancers: quoteDetails.childFreelancers,
			offlineIds: quoteDetails.offlineIds,
			isInternational: c.isInternational,
			category: c?.category,
			category_fr: c?.category_fr,
			categoryPrefix: c?.categoryPrefix,
			categoryUuid: c?.categoryUuid,
			subCategory: c?.category,
			subCategory_fr: c?.category_fr,
			subCategoryPrefix: c.categoryPrefix,
			subCategorySuffix: c.categoryPrefix,
			subCategoryUuid: `${c?.categoryUuid}-subInvoice`,
			categoryId: c.categoryId,
			subCategoryId: c.categoryId,
			costLineType: 'subCategoryTotal',
			item: `TOTAL - ${c?.categoryPrefix}: ${c?.category}`,
			item_fr: `TOTAL - ${c?.categoryPrefix}: ${c?.category_fr}`,
			exchangeRate: 1,
		} as ReduxRecapInfo
		newCostlinesArray.push(newSubCategoryTotalCostline)
		const newCategoryTotalCostline = {
			id: nanoid(),
			activeFlag: true,
			quoteId: quoteDetails.id,
			tenantId: quoteDetails.tenantId,
			freelancers: quoteDetails.freelancers,
			childFreelancers: quoteDetails.childFreelancers,
			offlineIds: quoteDetails.offlineIds,
			isInternational: c.isInternational,
			category: c?.category,
			category_fr: c?.category_fr,
			categoryPrefix: c?.categoryPrefix,
			categoryUuid: c?.categoryUuid,
			subCategory: c?.category,
			subCategory_fr: c?.category_fr,
			subCategoryPrefix: c.categoryPrefix,
			subCategorySuffix: c.categoryPrefix,
			subCategoryUuid: `${c?.categoryUuid}-subInvoice`,
			costLineType: 'categoryTotal',
			item: `TOTAL - ${c?.categoryPrefix}: ${c?.category}`,
			item_fr: `TOTAL - ${c?.categoryPrefix}: ${c?.category_fr}`,
			exchangeRate: 1,
		} as ReduxRecapInfo
		newCostlinesArray.push(newCategoryTotalCostline)
	})
	return newCostlinesArray
}

function createCostlinesForSubCategoryDefaults({
	categoriesArray,
	subCategoriesArray,
	quoteDetails,
}: {
	categoriesArray: Category[]
	subCategoriesArray: SubCategory[]
	quoteDetails: ReduxQuote
}) {
	// Go through each subcategory and add a subcategory Total line
	// Keep track of category Name if it changes we add previous category Total line before adding new subcategory Total
	let currentCategoryName = subCategoriesArray[0].category
	let previousCategoryName = subCategoriesArray[0].category
	let previousCategoryDetails = categoriesArray.find(
		(c) => c.category === previousCategoryName
	)
	const newCostlinesArray = [] as ReduxRecapInfo[]
	subCategoriesArray.map((sc, index) => {
		currentCategoryName = sc.category
		// We create the category Total for all categories except the last
		if (currentCategoryName !== previousCategoryName) {
			const currentCategoryDetails = categoriesArray.find(
				(c) => c.category === currentCategoryName
			)
			const newCategoryTotalCostline = {
				id: nanoid(),
				activeFlag: true,
				quoteId: quoteDetails.id,
				tenantId: quoteDetails.tenantId,
				freelancers: quoteDetails.freelancers,
				childFreelancers: quoteDetails.childFreelancers,
				offlineIds: quoteDetails.offlineIds,
				isInternational: previousCategoryDetails?.isInternational,
				category: previousCategoryDetails?.category,
				category_fr: previousCategoryDetails?.category_fr,
				categoryPrefix: previousCategoryDetails?.categoryPrefix,
				categoryUuid: previousCategoryDetails?.categoryUuid,
				subCategory: sc?.subCategory,
				subCategory_fr: sc?.subCategory_fr,
				subCategoryPrefix: sc.subCategoryPrefix,
				subCategorySuffix: sc.subCategorySuffix,
				subCategoryUuid: sc?.subCategoryUuid,
				costLineType: 'categoryTotal',
				item: `TOTAL - ${previousCategoryDetails?.categoryPrefix}: ${previousCategoryDetails?.category}`,
				item_fr: `TOTAL - ${previousCategoryDetails?.categoryPrefix}: ${previousCategoryDetails?.category_fr}`,
				exchangeRate: 1,
			} as ReduxRecapInfo
			newCostlinesArray.push(newCategoryTotalCostline)
			previousCategoryName = currentCategoryName
			previousCategoryDetails = currentCategoryDetails
		}
		// We add subcategory Total
		const newSubCategoryTotalCostline = {
			id: nanoid(),
			activeFlag: true,
			quoteId: quoteDetails.id,
			tenantId: quoteDetails.tenantId,
			freelancers: quoteDetails.freelancers,
			childFreelancers: quoteDetails.childFreelancers,
			offlineIds: quoteDetails.offlineIds,
			isInternational: previousCategoryDetails?.isInternational,
			category: previousCategoryDetails?.category,
			category_fr: previousCategoryDetails?.category_fr,
			categoryPrefix: previousCategoryDetails?.categoryPrefix,
			categoryUuid: previousCategoryDetails?.categoryUuid,
			subCategory: sc?.subCategory,
			subCategory_fr: sc?.subCategory_fr,
			subCategoryPrefix: sc.subCategoryPrefix,
			subCategorySuffix: sc.subCategorySuffix,
			subCategoryUuid: sc?.subCategoryUuid,
			costLineType: 'subCategoryTotal',
			item: `TOTAL - ${sc?.subCategoryPrefix}: ${sc?.subCategory}`,
			item_fr: `TOTAL - ${sc?.subCategoryPrefix}: ${sc?.subCategory_fr}`,
			exchangeRate: 1,
		} as ReduxRecapInfo
		newCostlinesArray.push(newSubCategoryTotalCostline)
		// We also add a category Total at the end
		if (subCategoriesArray.length - 1 === index) {
			const currentCategoryDetails = categoriesArray.find(
				(c) => c.category === currentCategoryName
			)
			// We create the category Total for all categories except the last
			const newCategoryTotalCostline = {
				id: nanoid(),
				activeFlag: true,
				quoteId: quoteDetails.id,
				tenantId: quoteDetails.tenantId,
				freelancers: quoteDetails.freelancers,
				childFreelancers: quoteDetails.childFreelancers,
				offlineIds: quoteDetails.offlineIds,
				isInternational: currentCategoryDetails?.isInternational,
				category: currentCategoryDetails?.category,
				category_fr: currentCategoryDetails?.category_fr,
				categoryPrefix: currentCategoryDetails?.categoryPrefix,
				categoryUuid: currentCategoryDetails?.categoryUuid,
				subCategory: sc?.subCategory,
				subCategory_fr: sc?.subCategory_fr,
				subCategoryPrefix: sc.subCategoryPrefix,
				subCategorySuffix: sc.subCategorySuffix,
				subCategoryUuid: sc?.subCategoryUuid,
				costLineType: 'categoryTotal',
				item: `TOTAL - ${currentCategoryDetails?.categoryPrefix}: ${currentCategoryDetails?.category}`,
				item_fr: `TOTAL - ${currentCategoryDetails?.categoryPrefix}: ${currentCategoryDetails?.category_fr}`,
				exchangeRate: 1,
			} as ReduxRecapInfo
			newCostlinesArray.push(newCategoryTotalCostline)
		}
	})
	return newCostlinesArray
}

export { createInvoiceQuoteCostlines }
